import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Một dòng của tờ lịch Vận khí: nhãn cố định bên trái, nội dung bên phải. Mọi khối trong
 * tờ lịch (việc nên/không nên, giờ, hướng, màu, số, tuổi, tiết, sao) dùng CÙNG khuôn này
 * để mắt quét theo một cột nhãn duy nhất thay vì đổi cách đọc ở mỗi khối.
 */
export function AlmanacRow({
  icon,
  label,
  children,
  className,
}: {
  icon?: ReactNode;
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-[5.5rem_minmax(0,1fr)] items-start gap-x-2 text-[13px] leading-5 sm:grid-cols-[6.5rem_minmax(0,1fr)]",
        className,
      )}
    >
      <span className="flex items-center gap-1.5 pt-[3px] text-[12px] leading-4 text-muted-foreground">
        {icon ? <span className="shrink-0 [&>svg]:size-3.5">{icon}</span> : null}
        {label}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** Nhóm dòng trong tờ lịch: gạch ngăn mảnh + khoảng thở đều nhau. */
export function AlmanacGroup({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("mt-4 space-y-2.5 border-t border-border/60 pt-4", className)}>
      {children}
    </div>
  );
}

const CHIP_TONE = {
  default: "border-gold-soft/60 text-foreground/90",
  good: "border-element-wood/30 bg-element-wood/[0.08] text-foreground/90",
  bad: "border-destructive/25 bg-destructive/[0.06] text-foreground/90",
} as const;

export function AlmanacChip({
  children,
  tone = "default",
  title,
}: {
  children: ReactNode;
  tone?: keyof typeof CHIP_TONE;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={cn("inline-flex rounded-full border px-2.5 py-0.5 text-[12px]", CHIP_TONE[tone])}
    >
      {children}
    </span>
  );
}

export function ChipList({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-1.5">{children}</div>;
}
